import * as yup from 'yup';

import { Login } from '@jupiter/shared';
import { Box, Typography } from '@material-ui/core';
import { FormProvider, useForm } from 'react-hook-form';
import { ROUTE_FORGOT_PASSWORD, ROUTE_REGISTRATION } from '../../navigation/routes';

import { yupResolver } from '@hookform/resolvers/yup';
import { ApiError, AUTH_BLOCKED, AUTH_DELETED, AUTH_NOT_VERIFIED, AUTH_UNAUTHORIZED } from '@jupiter/shared';
import { makeStyles } from '@material-ui/core/styles';
import { SerializedError } from '@reduxjs/toolkit/dist/createAsyncThunk';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/react';
import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import pablitaImage from '../../assets/pablita-867.png';
import AppTitle from '../../components/app-title';
import FormTextField from '../../components/forms/form-text-field';
import LoadingButton from '../../components/forms/loading-button';
import LanguageSelect from '../../components/language-select/language-select';
import VBox from '../../components/layout/vbox';
import Hyperlink from '../../components/navigation/hyperlink';
import useNavigation from '../../navigation/nav.hook';
import { useLoginMutation } from '../../redux/authentication/authentication.api';
import useAuthentication from '../../redux/authentication/authentication.hooks';

const useStyles = makeStyles(() => ({
  grid: {
    height: '100%',
  },
}));

function LoginError({ error }: { error?: SerializedError | FetchBaseQueryError | undefined }) {
  const { t } = useTranslation('common');

  if (!error) return null;

  let message = t('common.error-unknown');

  if ('status' in error) {
    const status = error.status;
    const data = error.data as ApiError;

    if (status === 400 || (status === 401 && data?.type === AUTH_UNAUTHORIZED)) {
      message = t('login-page.error-wrong-credentials');
    } else if (data?.type === AUTH_NOT_VERIFIED) {
      message = t('login-page.error-email-confirmation');
    } else if (data?.type === AUTH_BLOCKED) {
      message = t('login-page.error-locked-account');
    } else if (data?.type === AUTH_DELETED) {
      message = t('login-page.error-closed-account');
    }
  }

  return (
    <Typography variant="body1" component="p" color="error">
      {message}
    </Typography>
  );
}

export default function LoginPage() {
  const classes = useStyles();
  const { t, i18n } = useTranslation('common');
  const methods = useForm<Login>({
    resolver: yupResolver(
      yup
        .object({
          email: yup.string().email().required(),
          password: yup.string().required(),
          language: yup.string().default(i18n.language),
        })
        .required(),
    ),
  });
  const { isLoggedIn, setLoggedIn } = useAuthentication();
  const [login, { isLoading, error }] = useLoginMutation();
  const { navToHome } = useNavigation();

  useEffect(() => {
    if (isLoggedIn) {
      navToHome();
    }
  }, [isLoggedIn, navToHome]);

  const onSubmit = async (data: Login) => {
    try {
      await login(data).unwrap();
      setLoggedIn(true);
    } catch (e) {
      // Already handled
    }
  };

  return (
    <Box
      width={{ xs: '100%', sm: '100%', md: 376 }}
      height={{ xs: '100%', sm: '100%', md: 'auto' }}
      mx="auto"
      my={{ xs: 'auto', sm: 'auto', md: 0 }}
      paddingTop={{ xs: '28px', sm: '28px', md: '10vh' }}
    >
      <VBox spacing={0} className={classes.grid}>
        <VBox spacing={2}>
          <Box display="flex" justifyContent="center">
            <Box borderRadius={26} paddingX={2.5} paddingY={1.5} bgcolor="secondary.main" boxShadow={1}>
              <AppTitle />
            </Box>
          </Box>
          <Box display="flex" justifyContent="center">
            <img src={pablitaImage} alt="Main" />
          </Box>
          <Box textAlign="center" padding="20px">
            <Typography variant="h6">{t('login-page.heading-introduction')}</Typography>
          </Box>
        </VBox>
        <Box
          height="100%"
          textAlign="center"
          paddingY={2.5}
          paddingX={4.5}
          borderRadius={{ xs: '20px 20px 0 0', sm: '20px 20px 0 0', md: '20px 20px 20px 20px' }}
          boxShadow={{ xs: 1, sm: 1, md: 0 }}
          bgcolor="secondary.main"
        >
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <VBox spacing={2}>
                <FormTextField name="email" type="text" label={t('common.label-email')} fullWidth />
                <Box>
                  <FormTextField name="password" type="password" label={t('common.label-password')} fullWidth />
                  <Box mt={1} textAlign="right">
                    <Typography variant="body2" component="p" color="textPrimary">
                      <Hyperlink path={ROUTE_FORGOT_PASSWORD}>{t('login-page.link-forgot-password')}</Hyperlink>
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <LoginError error={error} />
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    isLoading={methods.formState.isSubmitting || isLoading}
                  >
                    {t('login-page.button-login')}
                  </LoadingButton>
                </Box>
              </VBox>
            </form>
          </FormProvider>
          <Box marginTop={4.25}>
            <Typography variant="body2" component="p" color="textPrimary">
              <Trans
                i18nKey="login-page.link-registration"
                components={[
                  <Hyperlink key="registration-link" path={ROUTE_REGISTRATION}>
                    Regista-te
                  </Hyperlink>,
                ]}
              ></Trans>
            </Typography>
          </Box>
        </Box>
        <Box marginTop={2} display="flex" flexDirection="row" justifyContent="center">
          <LanguageSelect />
        </Box>
      </VBox>
    </Box>
  );
}
